// Push an authenticated user's identity to the dataLayer so the HubSpot
// tracking tag (loaded via GTM — see index.html) can associate subsequent
// events with the known contact. Call this after /api/bootstrap returns
// an authenticated user.
//
// The payload is PLAINTEXT, not hashed — HubSpot's tag needs the raw
// email as its primary key to stitch anonymous page events back to an
// existing contact record. The dataLayer only runs in the authenticated
// user's own browser, so there is no server-side PII exposure; Cloudflare
// Worker logs never see these values.
//   - user_email: plaintext email (HubSpot primary key).
//   - user_id:   our internal user id (opaque UUID; useful for a future
//                CDP join if we add one).
//
// For anywhere we persist or log these values (lead_events, Workers
// Analytics Engine, Cloudflare console.log) we hash them first — see
// `hashEmail` in src/server/leadgen/hubspot.ts.
//
// The push is idempotent per page-load; calling it twice with the same
// identifiers is a silent no-op.

interface IdentifyArgs {
  email: string;
  userId: string;
  /** M13.7 — extra identity fields so the GTM HubSpot Identify tag can
   *  set custom properties on the contact (`ts_property_id` etc.). All
   *  optional — pre-M13.7 callers still work. */
  propertyId?: string | null;
  propertyName?: string | null;
  accountName?: string | null;
  grantMode?: string | null;
}

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

let lastIdentifiedUserId: string | null = null;

export function identifyHubSpotUser({
  email,
  userId,
  propertyId,
  propertyName,
  accountName,
  grantMode,
}: IdentifyArgs): void {
  if (typeof window === "undefined") return;
  if (lastIdentifiedUserId === userId) return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event: "identify",
    user_email: email,
    user_id: userId,
    property_id: propertyId ?? null,
    property_name: propertyName ?? null,
    account_name: accountName ?? null,
    grant_mode: grantMode ?? null,
  });
  lastIdentifiedUserId = userId;
}
